import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { EXTERNAL_LOT_API_REQUEST } from 'app/core/services/cte-lot-generator';
import { Observable, OperatorFunction, catchError, EMPTY, throwError, from, switchMap } from 'rxjs';
import { fetchAuthSession } from 'aws-amplify/auth';

let isRedirectingOn401 = false;

const handleErrors = (
  auth: AuthService,
  router: Router,
  isAuthCall: boolean,
  req: HttpRequest<unknown>,
): OperatorFunction<HttpEvent<unknown>, HttpEvent<unknown>> =>
  catchError((error: unknown) => {
    const isStaticAsset =
      /\.(js|css|chunk|png|jpg|jpeg|gif|svg|ico|woff|woff2|ttf|eot)$/.test(req.url) ||
      req.url.includes('chunk-') ||
      req.url.includes('assets/') ||
      (error instanceof HttpErrorResponse && error.headers?.get('content-type')?.includes('text/html'));

    if (error instanceof HttpErrorResponse && error.status === 401 && !isAuthCall && !isStaticAsset) {
      if (!isRedirectingOn401) {
        isRedirectingOn401 = true;
        auth.signOut();
        const currentUrl = router.url;
        router.navigate(['/sign-in'], { queryParams: { returnUrl: currentUrl } }).finally(() => {
          isRedirectingOn401 = false;
        });
      }
      return EMPTY as Observable<HttpEvent<unknown>>;
    }
    return throwError(() => error) as Observable<HttpEvent<unknown>>;
  });

export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  if (req.context.get(EXTERNAL_LOT_API_REQUEST)) return next(req);

  const auth = inject(AuthService);
  const router = inject(Router);

  const isAuthCall = /\/auth\//.test(req.url);

  const attachAndProceed = (token: string) => {
    const r = req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
    return next(r).pipe(handleErrors(auth, router, isAuthCall, req));
  };

  // Token present and valid → attach immediately
  if (auth.accessToken && !auth.isTokenExpired(auth.accessToken) && !isAuthCall) {
    return attachAndProceed(auth.accessToken);
  }

  // Token missing or expired → silent Amplify refresh, then attach
  if (!isAuthCall) {
    return from(fetchAuthSession()).pipe(
      switchMap((session) => {
        const idToken = session.tokens?.idToken?.toString();
        if (idToken) {
          auth.accessToken = idToken;
          return attachAndProceed(idToken);
        }
        return next(req).pipe(handleErrors(auth, router, isAuthCall, req));
      }),
      catchError(() => next(req).pipe(handleErrors(auth, router, isAuthCall, req))),
    );
  }

  // Auth calls — pass through without token
  return next(req).pipe(handleErrors(auth, router, isAuthCall, req));
};
